import { supabase } from './supabase';

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export interface UserDetails {
  id: string;
  email: string;
  business_name: string;
  contact_name: string;
  phone: string;
  address: string;
  admin_type: string;
  subscription_status: string;
  subscription_id: string;
  customer_id: string;
  onboarding_step: number;
  created_at: string;
  updated_at: string;
  service_types?: JsonValue;
  selected_tier?: JsonValue;
  selected_addons?: JsonValue;
  automation_requirements?: string;
  selected_platforms?: JsonValue;
  custom_ai_requirements?: string;
}

export interface SubscriptionDetails {
  user_id: string;
  user_email: string;
  subscription_status: string;
  subscription_id: string;
  customer_id: string;
  payment_sessions: JsonValue[];
}

export interface AuditLogRecord {
  id: string;
  action: string;
  table_name: string;
  record_id?: string | null;
  old_data?: JsonValue;
  new_data?: JsonValue;
  changed_by?: string | null;
  created_at: string;
}

export async function getUserFullDetails(userId: string): Promise<UserDetails | null> {
  try {
    const { data, error } = await supabase.rpc('get_user_full_details', {
      target_user_id: userId
    });

    if (error) throw error;
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw error;
  }
}

export async function getSubscriptionDetailsAdmin(userId: string): Promise<SubscriptionDetails | null> {
  try {
    const { data, error } = await supabase.rpc('get_subscription_details_admin', {
      target_user_id: userId
    });

    if (error) throw error;
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('Error fetching subscription details:', error);
    throw error;
  }
}

export async function updateUserInfo(
  userId: string,
  updates: {
    businessName?: string;
    contactName?: string;
    phone?: string;
    address?: string;
    email?: string;
  }
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('admin_update_user_info', {
      target_user_id: userId,
      new_business_name: updates.businessName || null,
      new_contact_name: updates.contactName || null,
      new_phone: updates.phone || null,
      new_address: updates.address || null,
      new_email: updates.email || null
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user info:', error);
    throw error;
  }
}

export async function deleteUser(
  userId: string,
  reason: string = 'Admin deletion'
): Promise<boolean> {
  try {
    // Get current session for authorization
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('Not authenticated');
    }

    const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`;

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        userId,
        reason
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete user');
    }

    return true;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
}

export async function canManageUser(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('admin_can_manage_user', {
      target_user_id: userId
    });

    if (error) throw error;
    return data || false;
  } catch (error) {
    console.error('Error checking user permissions:', error);
    return false;
  }
}

export async function getAuditLogs(limit: number = 50): Promise<AuditLogRecord[]> {
  try {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    throw error;
  }
}

export async function getUserAuditLogs(userId: string, limit: number = 20): Promise<AuditLogRecord[]> {
  try {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('record_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user audit logs:', error);
    throw error;
  }
}

export async function getUserStatistics(): Promise<{
  totalUsers: number;
  activeSubscriptions: number;
  pendingSubscriptions: number;
  canceledSubscriptions: number;
  totalAdmins: number;
  revenueMetrics?: {
    monthly: number;
    yearly: number;
  };
}> {
  try {
    const { data: users, error } = await supabase
      .from('users')
      .select('subscription_status, admin_type');

    if (error) throw error;

    const stats = {
      totalUsers: 0,
      activeSubscriptions: 0,
      pendingSubscriptions: 0,
      canceledSubscriptions: 0,
      totalAdmins: 0
    };

    users?.forEach(user => {
      if (user.admin_type === 'none' || !user.admin_type) {
        stats.totalUsers++;

        switch (user.subscription_status) {
          case 'active':
            stats.activeSubscriptions++;
            break;
          case 'pending':
            stats.pendingSubscriptions++;
            break;
          case 'canceled':
            stats.canceledSubscriptions++;
            break;
        }
      } else {
        stats.totalAdmins++;
      }
    });

    return stats;
  } catch (error) {
    console.error('Error fetching user statistics:', error);
    throw error;
  }
}

export function getStripeCustomerUrl(customerId: string): string {
  return `https://dashboard.stripe.com/customers/${customerId}`;
}

export function getStripeSubscriptionUrl(subscriptionId: string): string {
  return `https://dashboard.stripe.com/subscriptions/${subscriptionId}`;
}
